import { getCompatibleAuthTokenHttp } from 'src/services/utils/enhancedAuthTokenHttp';
import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { MutationFunction } from '@tanstack/react-query';
import { Tenant } from './types';
import {
  getTenancyMode,
  canCreateOrganizationTenant,
} from 'src/utils/tenancyMode';
import { DEFAULT_TENANT_TYPE, TenantType } from './tenantType';

export interface CreateTenantParams {
  name: string;
  magicLinkEnabled: boolean;
  isSuperTenant: boolean;
  tenantType: TenantType;
}

const createTenant: MutationFunction<Tenant, CreateTenantParams> = async (
  args,
) => {
  // Check tenancy mode restrictions before creating tenant
  const tenancyMode = getTenancyMode();

  if (tenancyMode === 'single') {
    // For single-tenant mode, we need to check if a tenant already exists
    // This will be validated on the server side as well
    try {
      const { default: getTenantsSimple } = await import('./getTenantsSimple');
      const existingTenants = await getTenantsSimple();

      if (!canCreateOrganizationTenant(existingTenants.tenants)) {
        throw new Error(
          'Cannot create tenant: Single-tenant mode allows only one organization tenant per deployment',
        );
      }
    } catch (error) {
      // If we can't check existing tenants, let the server handle validation
      console.warn(
        'Could not validate tenant count for single-tenant mode:',
        error,
      );
    }
  }

  if (isMockServices()) return await mock(_mockType);

  // Prepare request payload - server only expects name field
  const requestPayload = {
    name: args.name,
    // tenancyMode, // TODO: Include tenancy mode when backend API supports it
    configuration: {
      magicLinkAuth: {
        enabled: args.magicLinkEnabled,
      },
      isSuperTenant: args.isSuperTenant,
      tenantType: args.tenantType ?? DEFAULT_TENANT_TYPE,
    },
  };

  // Debug logging for request details (removed for production)

  try {
    // Server returns: {success: true, tenantId: '...', tenantSchema: '...', message: '...'}
    const authTokenHttp = await getCompatibleAuthTokenHttp();
    const response = await authTokenHttp<{
      success: boolean;
      tenantId: string;
      tenantSchema: string;
      message: string;
    }>(`/shared/tenants`, {
      method: 'POST',
      body: requestPayload,
    });

    if (response.data?.success) {
      // Transform server response to expected Tenant interface
      const tenant: Tenant = {
        id: response.data.tenantId,
        name: args.name,
        schema: response.data.tenantSchema,
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        configuration: {
          magicLinkAuth: {
            enabled: args.magicLinkEnabled,
          },
          isSuperTenant: args.isSuperTenant,
          tenantType: args.tenantType ?? DEFAULT_TENANT_TYPE,
        },
      };

      return tenant;
    }

    throw new Error('Failed to create tenant please try again.');
  } catch (error) {
    // Keep essential error logging for production debugging
    console.error('Tenant creation failed:', error);
    throw error; // Re-throw the original error to preserve server error details
  }
};

export default createTenant;

const _mockType: Tenant = {
  id: 'cm8nm5bke0000o10i7d0y6zbh',
  createdAt: '2025-03-24T22:03:10.669Z',
  name: 'razvan',
  schema: 'tenant_schema_cnyddd08bcab20n8ug3fmzfo',
  status: 'TODO',
  updatedAt: '2025-03-24T22:03:10.669Z',
  configuration: {
    magicLinkAuth: {
      enabled: false,
    },
  },
};
